import axios from 'axios';
import {logger} from '@/utils/logger';
import {camelizeObject, convertKeysToSnakeCase} from '@/utils';

/*
 * Api - API 호출 래핑 객체
 *
 * @params baseUrl<String> - https://live.afre..xx, vod 등 다양한 도메인의 api base host
 * @params withCredentials - 일부 api에 대해서는 Access-Control-Allow-Origin 이 '*' 형식으로 걸려있는게 있는데 이때 Access-Control-Allow-Credentials 를 true 보내는걸 동작하지 않도록 막고있다.
 * @params shouldConvertCaseAuto<Boolean> - snake case를 자동으로 camelCase로 변환해준다.
 * @params handleRequestDebugging<Function> - request 상세 분석용
 * @params handleResponseDebugging<Function> - camelCase로 변경 되기 전의 response 분석용
 *
 * @Methods get(url, queryParams): Promise
 * @Methods post(url, params, queryParams): Promise
 * */

/*
 * WARN : 고민해보실? 2
 * - https://tkdodo.eu/blog/you-might-not-need-react-query
 * - nextjs에서 react-query 꼭 필요한 것인가
 * */

type DebugHandler = (data: any) => void;

const ApiCore = (
  baseUrl: string,
  withCredentials: boolean = true,
  shouldPreventConvertingCase?: boolean,
  handleRequestDebugging?: DebugHandler,
  handleResponseDebugging?: DebugHandler,
  shouldSnakeCaseParams?: boolean,
  timeout?: number,
) => {
  const instance = axios.create({
    baseURL: baseUrl,
    timeout: timeout ?? 300000, //타임아웃 3초 설정
    withCredentials: withCredentials,
    headers: {post: {'Content-Type': 'application/x-www-form-urlencoded'}},
  });

  instance.interceptors.request.use(
    async (config) => {
      const isServer = typeof window === 'undefined';

      if (isServer) {
        const {cookies} = await import('next/headers');
        config.headers.cookie = cookies().toString();
      }

      handleRequestDebugging && handleRequestDebugging(config);
      return config;
    },
    (error) => {
      //handleRequestDebugging && handleRequestDebugging(false, error);
      const extraErrorData = {
        errorType: 'API Request Error',
        config: error.config
          ? {
              baseURL: error.config.baseURL,
              url: error.config.url,
              method: error.config.method,
              data: error.config.data,
              params: error.config.params,
            }
          : undefined,
      };

      logger.error(error, extraErrorData);
      return Promise.reject(error);
    },
  );

  instance.interceptors.response.use(
    (response) => {
      handleResponseDebugging && handleResponseDebugging(response);

      if (!shouldPreventConvertingCase) {
        response.data = camelizeObject(response.data);
      }

      return response;
    },
    (error) => {
      console.log('????', error);
      //handleResponseDebugging && handleResponseDebugging(false, error);
      const extraErrorData = {
        errorType: !error.response
          ? 'API Network Error' // 통신 중 발생한 저수준 네트워크 에러의 경우 별도 태깅
          : 'API Response Error',
        config: error.config
          ? {
              baseURL: error.config.baseURL,
              url: error.config.url,
              method: error.config.method,
              data: error.config.data,
              params: error.config.params,
            }
          : undefined,
        request: error.request,
        response: error.response
          ? {
              status: error.response.status,
              statusText: error.response.statusText,
              headers: error.response.headers,
              data: error.response.data,
            }
          : undefined,
        status: error.status,
      };

      logger.warn(error, extraErrorData);
      return Promise.reject(error);
    },
  );

  return {
    async get<T = any>(url: string, queryParams?: any): Promise<T> {
      try {
        const response = await instance.get(url, {
          params: shouldSnakeCaseParams
            ? convertKeysToSnakeCase(queryParams)
            : queryParams,
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    async post<T = any>(url, params, queryParams, options = {}) {
      try {
        const isFormData = params instanceof FormData;
        const snakeCaseQueryParams = isFormData
          ? params
          : shouldSnakeCaseParams
            ? convertKeysToSnakeCase(params)
            : params;

        const response = await instance.post(url, snakeCaseQueryParams, {
          ...options,
          params: queryParams,
        });

        return response.data as T;
      } catch (error) {
        throw error;
      }
    },
    async put<T = any>(url, params, queryParams) {
      try {
        const response = await instance.put(url, params, {
          params: queryParams,
        });
        return response.data as T;
      } catch (error) {
        throw error;
      }
    },
    async delete<T = any>(url, params, queryParams, options = {}) {
      try {
        const response = await instance.delete(url, {
          data: params,
          params: queryParams,
          ...options,
        });
        return response.data as T;
      } catch (error) {
        throw error;
      }
    },
  };
};

export default ApiCore;

export const PHPApiCore = (
  baseUrl: string,
  withCredentials: boolean = true,
  shouldPreventConvertingCase?: boolean,
  handleRequestDebugging?: DebugHandler,
  handleResponseDebugging?: DebugHandler,
  shouldSnakeCaseParams: boolean = true,
  timeout?: number,
) =>
  ApiCore(
    baseUrl,
    withCredentials,
    shouldPreventConvertingCase,
    handleRequestDebugging,
    handleResponseDebugging,
    shouldSnakeCaseParams,
    timeout,
  );

export const NODEApiCore = (
  baseUrl: string,
  withCredentials: boolean = true,
  shouldPreventConvertingCase?: boolean,
  handleRequestDebugging?: DebugHandler,
  handleResponseDebugging?: DebugHandler,
  shouldSnakeCaseParams: boolean = false,
  timeout?: number,
) =>
  ApiCore(
    baseUrl,
    withCredentials,
    shouldPreventConvertingCase,
    handleRequestDebugging,
    handleResponseDebugging,
    shouldSnakeCaseParams,
    timeout,
  );
